import { Pagination } from "services/search";

import {
  FleetTire,
  FleetTireEditFormValues,
  FleetTireRow,
  FleetTireSet,
  FleetTireSetEditFormValues,
  FleetTireSetRow,
  TireRadius,
  TireHeight,
  TireType,
  TireWidth,
  FleetTireAddRow,
  FleetTireAddFormValues,
  FleetTireSetWriteOffFormValues,
} from "./types";
import { createFleetDocumentAPI } from "services/api/fleet/vehicles/documents/api";
import { FLEET_TIRE_SET_DISPOSAL_DOCUMENT_TYPE_ID } from "./consts";

import { DataWithPagination } from "services/api/types";
import {
  transformFleetTire,
  transformFleetTireradiusRow,
  transformFleetTireHeightRow,
  transformFleetTireOnCreate,
  transformFleetTireOnUpdate,
  transformFleetTireRow,
  transformFleetTireSet,
  transformFleetTireSetOnCreate,
  transformFleetTireSetOnUpdate,
  transformFleetTireSetRow,
  transformFleetTireTypeRow,
  transformFleetTireWidthRow,
  transformFleetTireAddRow,
  transformFleetTireSetEditForm,
} from "./transformations";
import { createApiCall, paginatedApiCall } from "services/api/createApiCall";
import { transformErrorResponse } from "services/api/transformation";

/*---------------------------------------------------------------------------------------
  TIRE SET - SEARCH
-----------------------------------------------------------------------------------------*/

export const getFleetTiresSetAPI = async (
  pagination: Pagination,
  code?: string | null,
  vehicleId?: string | null,
  width?: number | null,
  height?: number | null,
  radius?: number | null,
  vehicleManufacturerId?: string | null,
  vehicleModelId?: string | null,
  vehicleTypeId?: string | null,
  tireManufacturerId?: string | null,
  warehouseId?: string | null,
  dot?: number | null,
  profileDepth?: number | null,
  tireTypeId?: string | null,
): Promise<DataWithPagination<FleetTireSetRow>> => {
  let path = `/v1/fleet/tires/sets?code=`;

  if (code) path += `${code}`;
  if (vehicleId) path += `&vehicleId=${vehicleId}`;
  if (width) path += `&width=${width}`;
  if (height) path += `&height=${height}`;
  if (radius) path += `&radius=${radius}`;
  if (vehicleManufacturerId) path += `&vehicleManufacturerId=${vehicleManufacturerId}`;
  if (vehicleModelId) path += `&vehicleModelId=${vehicleModelId}`;
  if (vehicleTypeId) path += `&vehicleTypeId=${vehicleTypeId}`;
  if (tireManufacturerId) path += `&tireManufacturerId=${tireManufacturerId}`;
  if (warehouseId) path += `&warehouseId=${warehouseId}`;
  if (dot) path += `&dot=${dot}`;
  if (profileDepth) path += `&profileDepth=${profileDepth}`;
  if (tireTypeId) path += `&tireTypeId=${tireTypeId}`;

  return await paginatedApiCall(path, pagination, transformFleetTireSetRow);
};

/*---------------------------------------------------------------------------------------
    TIRE SET - INFO
  -----------------------------------------------------------------------------------------*/

export const getFleetTireSetAPI = async (id: string): Promise<FleetTireSet> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/tires/sets/${id}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetTireSet(response.data);
};

/*---------------------------------------------------------------------------------------
    TIRE SET - ACTIVE
  -----------------------------------------------------------------------------------------*/

export const getFleetTireSetByVehicleActiveAPI = async (vehicleId: string): Promise<FleetTireSetEditFormValues> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/tires/sets/vehicle/${vehicleId}/active`,
      method: "GET",
    },
   
  )();

  return transformFleetTireSetEditForm(response.data);
};

/*---------------------------------------------------------------------------------------
    TIRE SET - PUBLISH
  -----------------------------------------------------------------------------------------*/

export const publishFleetTireSetAPI = async (
  id: string,
  t: (key: string) => string
): Promise<any> => {

  const response = await createApiCall(
    {
      path: `/v1/fleet/tires/sets/${id}/publish`,
      method: "PUT",
      body: { tireSetId: id },
    },
    {
      getMessage: {
        success: () => `${t("admin.fleet.tire.set.message.publish")}`,
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();


  return response.data;
};

/*---------------------------------------------------------------------------------------
    TIRE SET - WRITE OFF
  -----------------------------------------------------------------------------------------*/

export const writeOffFleetTireSetAPI = async (
  id: string,
  formValues: FleetTireSetWriteOffFormValues,
  t: (key: string) => string
): Promise<any> => {
  if (formValues.uploads.length > 0) {
    await createFleetDocumentAPI(
      id,
      "TireSet",
      {
        id: null,
        name: "Dokumentacija o zbrinjavanju",
        description: null,
        documentTypeId: FLEET_TIRE_SET_DISPOSAL_DOCUMENT_TYPE_ID,
        files: [],
      },
      formValues.uploads,
      t
    );
  }

  const response = await createApiCall(
    {
      path: `/v1/fleet/tires/sets/${id}/writeoff`,
      method: "POST",
      body: {
        ecologicallyDisposed: formValues.ecologicallyDisposed,
        writeOffDate: formValues.writeOffDate,
      },
    },
    {
      getMessage: {
        success: () => t("admin.fleet.tires.sets.writeOff.message.success"),
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data;
};

/*---------------------------------------------------------------------------------------
   TIRE SET -  CREATE
  -----------------------------------------------------------------------------------------*/

export const createFleetTireSetAPI = async (
  formValues: FleetTireSetEditFormValues,
  t: (key: string) => string
): Promise<FleetTireSet> => {
  const response = await createApiCall(
    {
      path: "/v1/fleet/tires/sets/",
      method: "POST",
      body: transformFleetTireSetOnCreate(formValues),
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetTireSet(response.data);
};

/*---------------------------------------------------------------------------------------
   TIRE SET -  EDIT
  -----------------------------------------------------------------------------------------*/

export const updateFleetTireSetAPI = async (
  id: string,
  formValues: FleetTireSetEditFormValues,
  t: (key: string) => string
): Promise<FleetTireSet> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/tires/sets/${id}`,
      method: "PUT",
      body: transformFleetTireSetOnUpdate(formValues),
    },
    {
      getMessage: {
        // success: () => t("admin.fleet.tire.set.message.update"),
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetTireSet(response.data);
};

/*---------------------------------------------------------------------------------------
   TIRE SET - DELETE
-----------------------------------------------------------------------------------------*/

export const deleteFleetTireSetAPI = async (
  id: string,
  t: (key: string) => string
): Promise<FleetTireSet> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/tires/sets/${id}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.fleet.tire.set.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};

/*---------------------------------------------------------------------------------------
  TIRE  - SEARCH
-----------------------------------------------------------------------------------------*/

export const getFleetTiresAPI = async (
  pagination: Pagination,
  name?: string | null,
  vehicleId?: string | null
): Promise<DataWithPagination<FleetTireRow>> => {
  let path = `/v1/fleet/tires?name=`;

  if (name) path += `${name}`;
  if (vehicleId) path += `&vehicleId=${vehicleId}`;


  return await paginatedApiCall(path, pagination, transformFleetTireRow);
};

/*---------------------------------------------------------------------------------------
    TIRE - INFO
  -----------------------------------------------------------------------------------------*/

export const getFleetTireAPI = async (id: string): Promise<FleetTire> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/tires/${id}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetTire(response.data);
};

/*---------------------------------------------------------------------------------------
   TIRE -  CREATE
  -----------------------------------------------------------------------------------------*/

export const createFleetTireAPI = async (
  formValues: FleetTireAddFormValues,
  t: (key: string) => string
): Promise<FleetTireAddRow[]> => {
  const response = await createApiCall(
    {
      path: "/v1/fleet/tires/",
      method: "POST",
      body: transformFleetTireOnCreate(formValues),
    },
    {
      getMessage: {
        // success: () => t("admin.fleet.tire.message.create"),
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();
  

  return response.data?.tires?.map(transformFleetTireAddRow);
};

/*---------------------------------------------------------------------------------------
   TIRE -  EDIT
  -----------------------------------------------------------------------------------------*/

export const updateFleetTireAPI = async (
  id: string,
  formValues: FleetTireEditFormValues,
  t: (key: string) => string
): Promise<FleetTireAddRow[]> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/tires/${id}`,
      method: "PUT",
      body: transformFleetTireOnUpdate(formValues),
    },
    {
      getMessage: {
        // success: () => t("admin.fleet.tire.message.update"),
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data?.tires?.map(transformFleetTireAddRow);
};

/*---------------------------------------------------------------------------------------
    DELETE
-----------------------------------------------------------------------------------------*/

export const deleteFleetTireAPI = async (
  id: string,
  t: (key: string) => string
): Promise<FleetTire> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/tires/${id}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.fleet.tire.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};

/*---------------------------------------------------------------------------------------
TIRE TYPE
-----------------------------------------------------------------------------------------*/

export const getTireTypesAPI = async (
  pagination: Pagination
): Promise<DataWithPagination<TireType>> => {
  let path = `/v1/catalog/tires/types`;
  return await paginatedApiCall(path, pagination, transformFleetTireTypeRow);
};

/*---------------------------------------------------------------------------------------
TIRE WIDTHS
-----------------------------------------------------------------------------------------*/

export const getTireWidthsAPI = async (
): Promise<TireWidth[]> => {
  const response = await createApiCall(
    {
      path: `/v1/catalog/tires/widths`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data.map(transformFleetTireWidthRow);
};

/*---------------------------------------------------------------------------------------
TIRE HEIGHTS
-----------------------------------------------------------------------------------------*/

export const getTireHeightsAPI = async (
): Promise<TireHeight[]> => {
  const response = await createApiCall(
    {
      path: `/v1/catalog/tires/heights`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data.map(transformFleetTireHeightRow);
};

/*---------------------------------------------------------------------------------------
TIRE radius
-----------------------------------------------------------------------------------------*/

export const getTireRadiusAPI = async (
): Promise<TireRadius[]> => {
  const response = await createApiCall(
    {
      path: `/v1/catalog/tires/diameters`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data.map(transformFleetTireradiusRow);
};

/*---------------------------------------------------------------------------------------
TIRE DIMENSIONS (cascading)
-----------------------------------------------------------------------------------------*/

export const getTireDimensionsAPI = async (
  width?: number | null,
  height?: number | null
): Promise<number[]> => {
  let path = `/v1/catalog/tires/dimensions`;
  const params: string[] = [];
  if (width) params.push(`width=${width}`);
  if (height) params.push(`height=${height}`);
  if (params.length > 0) path += `?${params.join("&")}`;

  const response = await createApiCall(
    {
      path,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data;
};

/*---------------------------------------------------------------------------------------
TIRE SET DEMOUNT
-----------------------------------------------------------------------------------------*/

export const demountFleetTireSetAPI = async (
  tireSetId: string,
  data: {
    targetWarehouseId: string;
    newTireSetId?: string | null;
    transferDate: Date | null;
    kmTraveled: number;
  },
  t: (key: string) => string
): Promise<any> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/tires/sets/${tireSetId}/demount`,
      method: "POST",
      body: data,
    },
    {
      getMessage: {
        success: () => "Demontaža seta guma je uspješno izvršena",
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data;
};

/*---------------------------------------------------------------------------------------
TIRE SET MOUNT
-----------------------------------------------------------------------------------------*/

export const mountFleetTireSetAPI = async (
  tireSetId: string,
  data: {
    targetVehicleId: string;
    oldSetWarehouseId?: string | null;
    transferDate: Date | null;
    kmTraveled: number;
  },
  t: (key: string) => string
): Promise<any> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/tires/sets/${tireSetId}/mount`,
      method: "POST",
      body: data,
    },
    {
      getMessage: {
        success: () => "Montaža seta guma je uspješno izvršena",
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data;
};

/*---------------------------------------------------------------------------------------
TIRE SET RELOCATE
-----------------------------------------------------------------------------------------*/

export const relocateFleetTireSetAPI = async (
  tireSetId: string,
  data: {
    targetWarehouseId: string;
    transferDate: Date | null;
  },
  t: (key: string) => string
): Promise<any> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/tires/sets/${tireSetId}/relocate`,
      method: "POST",
      body: data,
    },
    {
      getMessage: {
        success: () => "Premještaj seta guma je uspješno izvršen",
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data;
};
